// The Signal on the map, the moving part (Pixi): a relay that lights sends a comet from the network's side to the new star
// (the link draws itself behind it, particles fly off), the star takes a soft shockwave and the zone it covers brightens
// over a few seconds; the network beats slowly (a pulse leaves the capital and runs out along the links, hop by hop); the
// Silence eats the rim (a dark front with a ragged edge, embers dying on it, the stars beyond going out); an order lands on
// its star with a lock-on. Everything here is decoration over the PlayerView: the map's state never depends on it.
// Cost: a few dozen sprites at most, Graphics redrawn only while an effect runs; « Très bas » keeps the one-shot effects,
// shorter and without particles, no heartbeat and a still Silence.
import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import type { FxLevel } from './signal.js';

const SIGNAL = 0x7dd3fc;
const SIGNAL_HOT = 0xe6fbff;
const ORDER = 0xffd166;
const VOID = 0x010207;
const EMBER = 0x8f7bff;
/** The network's heartbeat: one beat every BEAT_MS, a hop every HOP_MS. */
const BEAT_MS = 6500, HOP_MS = 520;
const COVER_R = 320, COVER_ALPHA = 0.2;

interface Pt { x: number; y: number }
interface Comet { from: Pt; to: Pt; t: number; ms: number; head: Sprite; halo: Sprite; line: Graphics; lastEmit: number; done: () => void }
interface Spark { s: Sprite; vx: number; vy: number; life: number; age: number }
interface Ring { g: Graphics; x: number; y: number; t: number; ms: number; r0: number; r1: number; color: number; width: number; alpha: number; delay: number }
interface Flare { s: Sprite; t: number; ms: number; size: number; delay: number }
interface Streak { s: Sprite; g: Graphics | null; from: Pt; to: Pt; t: number; ms: number; delay: number; ring: boolean }
interface Lock { g: Graphics; x: number; y: number; t: number; ms: number; color: number }

export class SignalFx {
  /** Under the relays: the zone the network covers. */
  readonly cover = new Container();
  /** Over the sectors and their light, under the relays: the Silence. */
  readonly silence = new Container();
  /** Over the relays, under the stars: comets, streaks, sparks. */
  readonly fx = new Container();
  /** Over the stars: shockwaves, lock-ons. */
  readonly top = new Container();

  private comets: Comet[] = [];
  private sparks: Spark[] = [];
  private rings: Ring[] = [];
  private flares: Flare[] = [];
  private streaks: Streak[] = [];
  private locks: Lock[] = [];
  private coverOf = new Map<string, { s: Sprite; target: number; bump: number }>();
  private coverRamp = new Map<string, number>();
  private beatAt = 0;
  private beat: { edges: { from: Pt; to: Pt; depth: number }[]; nodes: { p: Pt; depth: number }[]; capital: Pt | null } = { edges: [], nodes: [], capital: null };
  // The Silence
  private dark = new Graphics();
  private edge: { s: Sprite; a: number; j: number; ph: number }[] = [];
  private embers: { s: Sprite; a: number; ph: number; drift: number }[] = [];
  /** A cold violet rim along the front: the last light, ragged. */
  private rim: { s: Sprite; a: number; ph: number }[] = [];
  private front = Infinity;
  private drawnFront = -1;
  private galaxyPx = 0;
  private stars: { s: Sprite; base: number; d: number }[] = [];
  private lastStarPass = 0;
  /** The world's scale at the last frame: effect sizes are meant in screen pixels. */
  private k = 0.4;
  private sp(screen: number): number { return screen / Math.max(0.08, this.k); }

  constructor(private readonly tex: { glow: Texture; dot: Texture; pulse: Texture }, readonly level: FxLevel) {
    this.silence.addChild(this.dark);
  }

  get on(): boolean { return this.level !== 'off'; }

  /** The zone my network covers: a soft wash on each of my connected stars. A star in `fresh` starts dark and brightens. */
  setCover(points: Map<string, Pt>): void {
    for (const [id, c] of this.coverOf) if (!points.has(id)) { c.s.destroy(); this.coverOf.delete(id); }
    for (const [id, p] of points) {
      let c = this.coverOf.get(id);
      if (!c) {
        const s = new Sprite(this.tex.glow);
        s.anchor.set(0.5); s.width = s.height = COVER_R * 2; s.tint = SIGNAL; s.blendMode = 'add';
        const fresh = this.coverRamp.has(id) && this.on;
        s.alpha = fresh ? 0 : COVER_ALPHA;
        this.cover.addChild(s);
        c = { s, target: COVER_ALPHA, bump: 0 };
        this.coverOf.set(id, c);
      }
      c.s.position.set(p.x, p.y);
    }
  }

  /** The next stars to join the cover brighten instead of appearing (called before setCover). */
  willLight(id: string): void { this.coverRamp.set(id, performance.now()); }

  /** The heartbeat's path: my lit relays oriented away from the capital, the depth of each end. */
  setBeat(edges: { from: Pt; to: Pt; depth: number }[], nodes: { p: Pt; depth: number }[], capital: Pt | null): void {
    this.beat = { edges, nodes, capital };
  }

  /** A relay lit: the comet runs from → to, then `onArrive` (the map redraws the relay as lit), the shockwave, the cover. */
  lightRelay(from: Pt, to: Pt, toId: string, onArrive: () => void): void {
    if (!this.on) { onArrive(); return; }
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    const ms = this.level === 'lite' ? 700 + len * 0.35 : 1100 + len * 0.9;
    const head = new Sprite(this.tex.pulse); head.anchor.set(0.5); head.width = head.height = this.sp(30); head.tint = SIGNAL_HOT; head.blendMode = 'add';
    const halo = new Sprite(this.tex.glow); halo.anchor.set(0.5); halo.width = halo.height = this.sp(120); halo.tint = SIGNAL; halo.alpha = 0.55; halo.blendMode = 'add';
    const line = new Graphics();
    this.fx.addChild(line, halo, head);
    head.position.set(from.x, from.y); halo.position.set(from.x, from.y);
    this.willLight(toId);
    this.comets.push({ from, to, t: 0, ms, head, halo, line, lastEmit: 0, done: () => { onArrive(); this.arrive(to, toId); } });
    this.wave(from, SIGNAL, 0, this.sp(10), this.sp(60), 600);
  }

  /** A relay started (a construction): a thin tracer along the dashes and a small ring at the far star. */
  startRelay(from: Pt, to: Pt): void {
    if (!this.on) return;
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    this.streak(from, to, 500 + len * 0.5, 0, SIGNAL, true);
  }

  /** An order landed on a star: four brackets close on it, two amber rings, a flare. */
  ack(p: Pt, color = ORDER): void {
    if (!this.on) return;
    const g = new Graphics(); this.top.addChild(g);
    this.locks.push({ g, x: p.x, y: p.y, t: 0, ms: this.level === 'lite' ? 1100 : 1900, color });
    this.wave(p, color, 0, this.sp(14), this.sp(80), 900, 3.5);
    if (this.level === 'full') { this.wave(p, color, 220, this.sp(14), this.sp(120), 1100); this.flare(p, this.sp(200), 800, 0, color); }
  }

  /** The Silence's front (world units from the galaxy's centre) over the starfield `stars`. Infinity hides it. */
  setSilence(front: number, galaxyPx: number, stars: readonly Sprite[]): void {
    // off: nothing is built (sprites would sit un-sized at the galaxy's centre, tick never runs to place or hide them)
    if (!this.on) return;
    this.front = front;
    this.galaxyPx = galaxyPx;
    if (this.stars.length !== stars.length) this.stars = stars.map((s) => ({ s, base: s.alpha, d: Math.hypot(s.x, s.y) }));
    if (!this.edge.length && Number.isFinite(front)) this.buildEdge();
  }

  private buildEdge(): void {
    const n = this.level === 'full' ? 96 : 40;
    for (let i = 0; i < n; i++) {
      const s = new Sprite(this.tex.glow); s.anchor.set(0.5); s.tint = VOID; s.alpha = 0.95;
      this.silence.addChild(s);
      this.edge.push({ s, a: (i / n) * Math.PI * 2, j: ((i * 7919) % 97) / 97, ph: ((i * 104729) % 89) / 89 * Math.PI * 2 });
    }
    for (let i = 0, m = this.level === 'full' ? 72 : 36; i < m; i++) {
      const s = new Sprite(this.tex.glow); s.anchor.set(0.5); s.tint = 0x4a3c9a; s.blendMode = 'add';
      this.silence.addChild(s);
      this.rim.push({ s, a: (i / m) * Math.PI * 2 + (((i * 7919) % 13) / 13) * 0.05, ph: ((i * 2654435761) % 1000) / 1000 });
    }
    if (this.level !== 'full') return;
    for (let i = 0; i < 140; i++) {
      const s = new Sprite(this.tex.dot); s.anchor.set(0.5); s.tint = EMBER; s.blendMode = 'add';
      this.silence.addChild(s);
      this.embers.push({ s, a: ((i * 2654435761) % 1000) / 1000 * Math.PI * 2, ph: ((i * 40503) % 1000) / 1000, drift: 0 });
    }
  }

  private drawDark(): void {
    const g = this.dark, r = this.front;
    g.clear();
    if (!Number.isFinite(r)) { for (const e of this.edge) e.s.visible = false; for (const e of this.rim) e.s.visible = false; for (const e of this.embers) e.s.visible = false; this.drawnFront = r; return; }
    const outer = Math.max(r * 2.5, this.galaxyPx * 3), band = this.galaxyPx * 0.3, N = 28;
    // a soft gradient band at the front, then the void
    for (let i = 0; i < N; i++) {
      const rr = r + band * (i + 0.5) / N;
      g.circle(0, 0, rr).stroke({ color: VOID, width: band / N + 1, alpha: 0.04 + 0.9 * ((i + 1) / N) ** 1.8 });
    }
    g.circle(0, 0, (r + band + outer) / 2).stroke({ color: VOID, width: outer - r - band + 4, alpha: 0.94 });
    // the front's line: a cold violet, faint (what light is left)
    g.circle(0, 0, r).stroke({ color: EMBER, width: 3, alpha: 0.18 });
    this.drawnFront = r;
  }

  private arrive(p: Pt, id: string): void {
    const lite = this.level === 'lite';
    this.wave(p, SIGNAL_HOT, 0, this.sp(8), this.sp(lite ? 70 : 120), lite ? 700 : 1300, 5);
    if (!lite) { this.wave(p, SIGNAL, 180, this.sp(8), this.sp(180), 1700, 3); this.flare(p, this.sp(260), 1300, 0, SIGNAL); }
    const c = this.coverOf.get(id);
    if (c) c.bump = lite ? 0.15 : 0.35;
  }

  private wave(p: Pt, color: number, delay: number, r0: number, r1: number, ms: number, width = 3, alpha = 0.9): void {
    const g = new Graphics(); this.top.addChild(g);
    this.rings.push({ g, x: p.x, y: p.y, t: 0, ms, r0, r1, color, width, alpha, delay });
  }

  private flare(p: Pt, size: number, ms: number, delay: number, color: number): void {
    const s = new Sprite(this.tex.glow); s.anchor.set(0.5); s.position.set(p.x, p.y); s.tint = color; s.blendMode = 'add'; s.alpha = 0; s.width = s.height = 1;
    this.top.addChild(s);
    this.flares.push({ s, t: 0, ms, size, delay });
  }

  private streak(from: Pt, to: Pt, ms: number, delay: number, color: number, ring: boolean): void {
    const s = new Sprite(this.tex.pulse); s.anchor.set(0.5); s.tint = color; s.blendMode = 'add'; s.visible = false;
    s.rotation = Math.atan2(to.y - from.y, to.x - from.x); s.width = 90; s.height = 26;
    // the link lights behind the pulse (a short-lived line), so the beat reads as the network itself pulsing
    const g = this.level === 'full' ? new Graphics() : null;
    if (g) this.fx.addChildAt(g, 0);
    this.fx.addChild(s);
    this.streaks.push({ s, g, from, to, t: 0, ms, delay, ring });
  }

  private heartbeat(): void {
    const { edges, nodes, capital } = this.beat;
    if (!capital || !edges.length) return;
    this.wave(capital, SIGNAL, 0, this.sp(12), this.sp(70), 1200, 3, 0.7);
    for (const e of edges) {
      const len = Math.hypot(e.to.x - e.from.x, e.to.y - e.from.y);
      this.streak(e.from, e.to, Math.max(HOP_MS, len * 1.1), e.depth * HOP_MS, SIGNAL, false);
    }
    for (const n of nodes) if (n.depth > 0) { this.wave(n.p, SIGNAL, n.depth * HOP_MS + 120, this.sp(8), this.sp(42), 900, 2.5, 0.75); this.flare(n.p, this.sp(70), 700, n.depth * HOP_MS + 60, SIGNAL); }
  }

  /** Per frame; `k` is the world's scale (widths in screen pixels). */
  tick(dt: number, k: number): void {
    if (!this.on) return;
    this.k = k;
    const px = 1 / Math.max(0.08, k), now = performance.now();
    // the zone covered brightens
    for (const [id, c] of this.coverOf) {
      const ramp = this.coverRamp.get(id);
      let a = c.target;
      if (ramp !== undefined) { const u = (now - ramp) / 3800; if (u >= 1) this.coverRamp.delete(id); else a = c.target * Math.max(0, u) ** 1.5; }
      c.bump = Math.max(0, c.bump - dt / 2600);
      c.s.alpha = a + c.bump;
    }
    for (const c of this.comets.slice()) {
      c.t = Math.min(1, c.t + dt / c.ms);
      const e = c.t < 0.5 ? 2 * c.t * c.t : 1 - (-2 * c.t + 2) ** 2 / 2;
      const x = c.from.x + (c.to.x - c.from.x) * e, y = c.from.y + (c.to.y - c.from.y) * e;
      c.head.position.set(x, y); c.halo.position.set(x, y);
      c.line.clear();
      c.line.moveTo(c.from.x, c.from.y).lineTo(x, y).stroke({ color: SIGNAL, width: 20 * px, alpha: 0.35 });
      c.line.moveTo(c.from.x, c.from.y).lineTo(x, y).stroke({ color: SIGNAL_HOT, width: 5 * px, alpha: 1 });
      if (this.level === 'full' && now - c.lastEmit > 22 && this.sparks.length < 140) {
        c.lastEmit = now;
        const dx = c.to.x - c.from.x, dy = c.to.y - c.from.y, l = Math.hypot(dx, dy) || 1;
        for (let i = 0; i < 2; i++) {
          const s = new Sprite(this.tex.dot); s.anchor.set(0.5); s.tint = i ? SIGNAL : SIGNAL_HOT; s.blendMode = 'add';
          s.width = s.height = (3 + Math.random() * 5) * px; s.position.set(x, y);
          this.fx.addChild(s);
          const side = (Math.random() - 0.5) * 0.16, back = -0.03 - Math.random() * 0.05;
          this.sparks.push({ s, vx: (-dy / l) * side + (dx / l) * back, vy: (dx / l) * side + (dy / l) * back, life: 500 + Math.random() * 500, age: 0 });
        }
      }
      if (c.t >= 1) {
        this.comets.splice(this.comets.indexOf(c), 1);
        c.head.destroy(); c.halo.destroy();
        // the drawn line lingers and fades into the map's own lit relay
        this.fadeOut(c.line, 700);
        c.done();
      }
    }
    for (const s of this.sparks.slice()) {
      s.age += dt;
      if (s.age >= s.life) { s.s.destroy(); this.sparks.splice(this.sparks.indexOf(s), 1); continue; }
      s.s.x += s.vx * dt * px * 1.2; s.s.y += s.vy * dt * px * 1.2;
      s.s.alpha = 1 - s.age / s.life;
    }
    for (const r of this.rings.slice()) {
      r.t += dt;
      const u = (r.t - r.delay) / r.ms;
      r.g.clear();
      if (u < 0) continue;
      if (u >= 1) { r.g.destroy(); this.rings.splice(this.rings.indexOf(r), 1); continue; }
      const e = 1 - (1 - u) ** 3;
      r.g.circle(r.x, r.y, (r.r0 + (r.r1 - r.r0) * e)).stroke({ color: r.color, width: r.width * (1 - u * 0.7) * px, alpha: r.alpha * (1 - u) });
    }
    for (const f of this.flares.slice()) {
      f.t += dt;
      const u = (f.t - f.delay) / f.ms;
      if (u < 0) continue;
      if (u >= 1) { f.s.destroy(); this.flares.splice(this.flares.indexOf(f), 1); continue; }
      const up = Math.min(1, u / 0.18);
      f.s.width = f.s.height = f.size * (0.3 + 0.7 * up);
      f.s.alpha = 0.75 * up * (1 - u) ** 1.2;
    }
    for (const st of this.streaks.slice()) {
      st.t += dt;
      const u = (st.t - st.delay) / st.ms;
      if (u < 0) continue;
      if (u >= 1) {
        st.s.destroy(); this.streaks.splice(this.streaks.indexOf(st), 1);
        if (st.g) this.fadeOut(st.g, 500);
        if (st.ring) this.wave(st.to, SIGNAL, 0, this.sp(8), this.sp(50), 800, 2.5);
        continue;
      }
      st.s.visible = true;
      const hx = st.from.x + (st.to.x - st.from.x) * u, hy = st.from.y + (st.to.y - st.from.y) * u;
      st.s.position.set(hx, hy);
      if (st.g) {
        const tx = st.from.x + (st.to.x - st.from.x) * Math.max(0, u - 0.45), ty = st.from.y + (st.to.y - st.from.y) * Math.max(0, u - 0.45);
        st.g.clear();
        st.g.moveTo(tx, ty).lineTo(hx, hy).stroke({ color: SIGNAL, width: 14 * px, alpha: 0.3 });
        st.g.moveTo(tx, ty).lineTo(hx, hy).stroke({ color: SIGNAL_HOT, width: 4 * px, alpha: 0.85 });
      }
      st.s.alpha = Math.min(1, Math.sin(u * Math.PI) * 1.3);
      st.s.width = 80 * px; st.s.height = 22 * px;
    }
    for (const l of this.locks.slice()) {
      l.t += dt;
      const u = l.t / l.ms;
      l.g.clear();
      if (u >= 1) { l.g.destroy(); this.locks.splice(this.locks.indexOf(l), 1); continue; }
      const close = Math.min(1, u / 0.25), ease = 1 - (1 - close) ** 3;
      const half = this.sp(64 - 34 * ease), arm = this.sp(11), a = u < 0.75 ? 1 : 1 - (u - 0.75) / 0.25;
      const blink = u > 0.25 && u < 0.75 ? 0.75 + 0.25 * Math.sin((u - 0.25) * 40) : 1;
      for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        const cx = l.x + sx * half, cy = l.y + sy * half;
        l.g.moveTo(cx, cy - sy * arm).lineTo(cx, cy).lineTo(cx - sx * arm, cy);
      }
      l.g.stroke({ color: l.color, width: 4 * px, alpha: a * blink });
    }
    // the heartbeat
    if (this.level === 'full') {
      if (!this.beatAt) this.beatAt = now + 1800;
      if (now >= this.beatAt) { this.beatAt = now + BEAT_MS; this.heartbeat(); }
    }
    // the Silence
    if (Number.isFinite(this.front) && Math.abs(this.front - this.drawnFront) > 2) this.drawDark();
    if (Number.isFinite(this.front)) {
      const r = this.front, size = this.galaxyPx * 0.34;
      for (const e of this.edge) {
        e.s.visible = true;
        const wob = this.level === 'full' ? Math.sin(now / 2400 + e.ph) * 0.5 + Math.sin(now / 1100 + e.ph * 3) * 0.2 : 0;
        // tongues of dark reach in from the front, each at its pace: a ragged edge, not a circle
        const rr = r + size * (-0.12 + 0.35 * e.j + 0.22 * wob);
        e.s.position.set(Math.cos(e.a) * rr, Math.sin(e.a) * rr);
        e.s.width = e.s.height = size * (0.7 + 0.9 * e.j);
      }
      for (const m of this.rim) {
        m.s.visible = true;
        const wob = this.level === 'full' ? Math.sin(now / 1900 + m.ph * 9) : 0;
        const rr = r + size * (0.02 + 0.1 * wob + 0.12 * m.ph);
        m.s.position.set(Math.cos(m.a) * rr, Math.sin(m.a) * rr);
        m.s.width = m.s.height = size * (0.35 + 0.3 * m.ph);
        m.s.alpha = 0.45 + 0.2 * wob;
      }
      for (const m of this.embers) {
        m.s.visible = true;
        // an ember drifts in from the void, glows on the front, dies inside it: the light going out
        const life = ((now / 5200 + m.ph) % 1);
        const rr = r + size * 0.35 - life * size * 0.5;
        m.s.position.set(Math.cos(m.a + life * 0.05) * rr, Math.sin(m.a + life * 0.05) * rr);
        m.s.width = m.s.height = (4 + 8 * Math.sin(life * Math.PI)) * px;
        m.s.alpha = 0.85 * Math.sin(life * Math.PI) * (0.6 + 0.4 * Math.sin(now / 90 + m.ph * 50));
      }
      if (now - this.lastStarPass > 120) {
        this.lastStarPass = now;
        for (const st of this.stars) {
          const over = st.d - r;
          let target = st.base;
          if (over > 40) target = 0;
          else if (over > -70) target = st.base * (this.level === 'full' ? (0.25 + 0.75 * Math.random()) * (1 - (over + 70) / 110) : 0.4); // flickering on the front
          st.s.alpha += (target - st.s.alpha) * 0.35;
        }
      }
    }
  }

  private fadeOut(g: Graphics, ms: number): void {
    let t = 0;
    const tick = (): void => { t += 16; g.alpha = 1 - t / ms; if (t >= ms) g.destroy(); else requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }

  /** Debug and capture: the next heartbeat now. */
  beatNow(): void { this.beatAt = performance.now(); }

  /** Whether a one-shot effect is running (the map keeps animating while so). */
  get busy(): boolean { return this.comets.length + this.rings.length + this.streaks.length + this.locks.length > 0; }
}
